import { useCallback, useEffect, useRef, useState } from "react";
import { getMetricKeyForTab } from "../components/MetricConfig";
import { useApiClient } from "../../../api";

export function useFetchMetrics(school = null, subscriptionCategory = "ALL") {
  const [metricOptions, setMetricOptions] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [metricsScope, setMetricsScope] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [error, setError] = useState(null);
  const ApiClient = useApiClient();
  const schoolId = school?.id;
  const scope = `${schoolId || "all"}:${subscriptionCategory}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const optionsRequest = useRef(0);
  const metricsRequest = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      optionsRequest.current += 1;
      metricsRequest.current += 1;
    };
  }, []);

  const fetchMetricOptions = useCallback(
    async (tab) => {
      const request = ++optionsRequest.current;
      ++metricsRequest.current;
      setOptionsLoading(true);
      setMetricsLoading(false);
      setMetricOptions([]);
      setMetrics([]);
      setError(null);
      try {
        const response = await ApiClient.core.fetchMetricOptions({
          metricType: getMetricKeyForTab(tab),
          schoolMetrics: !!schoolId,
        });
        if (mounted.current && request === optionsRequest.current) {
          setMetricOptions(response.metricOptions || []);
        }
      } catch (error) {
        if (mounted.current && request === optionsRequest.current) {
          setError(error);
        }
      } finally {
        if (mounted.current && request === optionsRequest.current) {
          setOptionsLoading(false);
        }
      }
    },
    [ApiClient, schoolId],
  );

  const fetchMetrics = useCallback(
    async (metric, tab) => {
      const request = ++metricsRequest.current;
      const isCurrent = () =>
        mounted.current &&
        request === metricsRequest.current &&
        currentScope.current === scope;
      setMetricsLoading(true);
      setError(null);
      setMetrics([]);
      try {
        const response = await ApiClient.core.fetchMetrics({
          metricType: getMetricKeyForTab(tab),
          metric,
          school: schoolId,
          subscriptionCategory,
        });
        if (isCurrent()) {
          setMetrics(response.metrics);
          setMetricsScope(scope);
        }
      } catch (error) {
        if (isCurrent()) setError(error);
      } finally {
        if (isCurrent()) setMetricsLoading(false);
      }
    },
    [ApiClient, schoolId, subscriptionCategory, scope],
  );

  return {
    metrics: metricsScope === scope ? metrics : [],
    metricOptions,
    loading: optionsLoading || metricsLoading,
    error,
    fetchMetricOptions,
    fetchMetrics,
  };
}
